import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { getApiError } from "../api/errors";
import {
  finalizeNativePosSale,
  getNativePosDiscountCapability,
  lookupNativePosBarcode,
  searchNativePosProducts,
} from "../api/nativePos";
import { previewPricing } from "../api/pricing";
import PageHeader from "../components/PageHeader";
import PosProductImage from "../components/PosProductImage";
import MainLayout from "../layouts/MainLayout";
import { useAuth } from "../context/AuthContext";
import { useConnectivity } from "../context/ConnectivityContext";
import useDebouncedValue from "../hooks/useDebouncedValue";
import { createIdempotencyKey, formatDate, formatMoney } from "../utils/format";
import { getPrinterPaperWidth } from "../utils/hardwareReadiness";
import { printNativePosReceipt } from "../utils/receiptPrinter";
import { queueStoreBridgeSaleReceipt } from "../api/storeBridge";
import { createStoreBridgeJobKey, getSelectedStoreBridgeDeviceId } from "../utils/storeBridge";
import { concreteIdentityKey } from "../utils/productVariants";
import { quickDiscountPercents, validDiscountPercent } from "../utils/posDiscounts";
import {
  getOfflineSnapshotMetadata,
  getOfflineSnapshotStatusMetadata,
  invalidateOfflineCatalogRuntimeCache,
  lookupOfflinePosBarcode,
  searchOfflinePosProducts,
} from "../offline/catalog";
import {
  completeOfflineCashSale,
  getOfflineSaleOperationalStatus,
  getOfflineSaleQueueSummary,
} from "../offline/sales";
import { getOfflineAuthorizationStatus } from "../offline/session";

const SEARCH_LIMIT = 24;
const MAX_LINE_QUANTITY = 1_000_000;

function readableOfflineReason(value) {
  return String(value || "Offline reconciliation required")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function localDateTime(value, fallback = "Never") {
  if (!value) return fallback;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? fallback : parsed.toLocaleString();
}

function normalizedAvailable(item) {
  return Math.max(0, Number(item?.available_quantity || 0));
}

function normalizedPrice(item) {
  return Number(item?.price || 0);
}

function cartLineFromProduct(item) {
  return {
    identity_key: concreteIdentityKey(item.product_public_id, item.product_variant_public_id),
    product_public_id: item.product_public_id,
    product_variant_public_id: item.product_variant_public_id || null,
    variant_label: item.variant_label || "",
    variant_options: item.variant_options || null,
    sale_claim: item.sale_claim || null,
    track_inventory: item.track_inventory !== false,
    product_name: item.product_name,
    brand: item.brand || "",
    category: item.category || "",
    sku: item.sku || "",
    price: normalizedPrice(item),
    physical_quantity: item.track_inventory === false ? null : Number(item.physical_quantity || 0),
    reserved_quantity: item.track_inventory === false ? null : Number(item.reserved_quantity || 0),
    available_quantity: item.track_inventory === false ? null : normalizedAvailable(item),
    quantity: 1,
  };
}

export default function PointOfSale() {
  const { t } = useTranslation();
  const { selectedWorkspace, selectedStore, user, offlineAuthActive } = useAuth();
  const { state: connectivityState, isOnline } = useConnectivity();
  const scannerRef = useRef(null);
  const cartRef = useRef([]);
  const [scannerCode, setScannerCode] = useState("");
  const [scannerBusy, setScannerBusy] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchBusy, setSearchBusy] = useState(false);
  const [cart, setCart] = useState([]);
  const [pricingMode, setPricingMode] = useState("RETAIL");
  const [discountPercent, setDiscountPercent] = useState("");
  const [customDiscountOpen, setCustomDiscountOpen] = useState(false);
  const [discountCapability, setDiscountCapability] = useState(null);
  const [discountCapabilityError, setDiscountCapabilityError] = useState("");
  const [discountPreview, setDiscountPreview] = useState(null);
  const [discountPreviewBusy, setDiscountPreviewBusy] = useState(false);
  const [discountPreviewError, setDiscountPreviewError] = useState("");
  const [belowCostConfirmation, setBelowCostConfirmation] = useState(null);
  const [transactionKey, setTransactionKey] = useState(() => createIdempotencyKey("native-pos"));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [snapshotMeta, setSnapshotMeta] = useState(null);
  const [offlineStatusSnapshot, setOfflineStatusSnapshot] = useState(null);
  const [offlineOperational, setOfflineOperational] = useState({
    sales: [],
    lastSuccessfulSyncAt: null,
    lastAcknowledgedSyncAt: null,
    deviceMismatchedPending: 0,
    cashierMismatchedPending: 0,
  });
  const [offlineAuthorization, setOfflineAuthorization] = useState({
    deviceStatus: "not_enrolled",
    authorizationState: "not_enrolled",
    authorizationError: null,
    grantStatus: "missing",
    grantExpiresAt: null,
  });
  const [showAllOfflineIssues, setShowAllOfflineIssues] = useState(false);
  const [offlineQueue, setOfflineQueue] = useState({
    pending: 0,
    syncing: 0,
    reconciliation: 0,
    failed: 0,
    syncedRecently: 0,
    total: 0,
  });
  const debouncedSearch = useDebouncedValue(searchQuery.trim(), 260);
  const discountRequestKey = useMemo(() => JSON.stringify({
    discountPercent,
    pricingMode,
    items: cart.map((line) => [line.product_public_id, line.product_variant_public_id, line.quantity]),
  }), [cart, discountPercent, pricingMode]);
  const currentDiscountPreview = discountPreview?.key === discountRequestKey
    ? discountPreview.data : null;
  useEffect(() => {
    setBelowCostConfirmation(null);
  }, [discountRequestKey]);
  const groupedSearchResults = useMemo(() => {
    const groups = new Map();
    searchResults.forEach((item) => {
      if (!groups.has(item.product_public_id)) groups.set(item.product_public_id, []);
      groups.get(item.product_public_id).push(item);
    });
    return [...groups.entries()];
  }, [searchResults]);

  const refocusScanner = useCallback(() => {
    window.setTimeout(() => scannerRef.current?.focus(), 0);
  }, []);

  useEffect(() => {
    cartRef.current = cart;
  }, [cart]);

  useEffect(() => {
    refocusScanner();
  }, [refocusScanner]);

  useEffect(() => {
    cartRef.current = [];
    setCart([]);
    setScannerCode("");
    setSearchQuery("");
    setSearchResults([]);
    setReceipt(null);
    setDiscountPercent("");
    setCustomDiscountOpen(false);
    setBelowCostConfirmation(null);
    setError("");
    setNotice("");
    setTransactionKey(createIdempotencyKey("native-pos"));
    refocusScanner();
  }, [selectedStore?.public_id, selectedWorkspace?.public_id, refocusScanner]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setDiscountCapability(null);
    setDiscountCapabilityError("");
    if (isOnline) {
      getNativePosDiscountCapability({ signal: controller.signal })
        .then((response) => {
          if (active) setDiscountCapability(response.data);
        })
        .catch((requestError) => {
          if (active && requestError?.code !== "ERR_CANCELED") {
            setDiscountCapabilityError(getApiError(requestError, t("pos.discountCapabilityError")));
          }
        });
    } else {
      setDiscountPercent("");
      setCustomDiscountOpen(false);
    }
    return () => {
      active = false;
      controller.abort();
    };
  }, [isOnline, selectedStore?.public_id, selectedWorkspace?.public_id, t]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setDiscountPreview(null);
    setDiscountPreviewError("");
    setDiscountPreviewBusy(false);
    if (!isOnline || !cart.length || (!discountPercent && pricingMode === "RETAIL")) return undefined;
    if (discountPercent && !validDiscountPercent(discountPercent, discountCapability?.effective_max_discount_percent)) {
      setDiscountPreviewError(t("pos.discountLimitError"));
      return undefined;
    }
    setDiscountPreviewBusy(true);
    previewPricing(cart.map((line) => ({
      product_public_id: line.product_public_id,
      product_variant_public_id: line.product_variant_public_id,
      quantity: line.quantity,
      ...(discountPercent ? { discount_percent: discountPercent } : {}),
    })), { signal: controller.signal, pricingMode })
      .then((response) => {
        if (active) setDiscountPreview({ key: discountRequestKey, data: response.data });
      })
      .catch((requestError) => {
        if (active && requestError?.code !== "ERR_CANCELED") {
          const detail = requestError?.response?.data?.detail;
          if (detail?.code === "WHOLESALE_PRICE_NOT_CONFIGURED") {
            setDiscountPreviewError(t("pos.wholesaleNotConfiguredFull"));
          } else if (detail?.code === "WHOLESALE_MINIMUM_QUANTITY_NOT_MET") {
            setDiscountPreviewError(t("pos.wholesaleMinimumFull", { count: detail.minimum_quantity }));
          } else {
            setDiscountPreviewError(getApiError(requestError, t("pos.discountPreviewError")));
          }
        }
      })
      .finally(() => {
        if (active) setDiscountPreviewBusy(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [cart, discountCapability?.effective_max_discount_percent, discountPercent, discountRequestKey, isOnline, pricingMode, t]);

  useEffect(() => {
    let active = true;
    if (isOnline) {
      setSnapshotMeta(null);
      return undefined;
    }
    getOfflineSnapshotMetadata(
      selectedWorkspace?.public_id,
      selectedStore?.public_id,
    )
      .then((metadata) => {
        if (active) setSnapshotMeta(metadata);
      })
      .catch(() => {
        if (active) setSnapshotMeta(null);
      });
    return () => {
      active = false;
    };
  }, [isOnline, selectedStore?.public_id, selectedWorkspace?.public_id]);

  useEffect(() => {
    let active = true;
    const refreshOfflineStatus = () => {
      const workspaceId = selectedWorkspace?.public_id;
      const storeId = selectedStore?.public_id;
      Promise.all([
        getOfflineSaleQueueSummary(workspaceId, storeId),
        getOfflineSaleOperationalStatus(workspaceId, storeId),
        getOfflineAuthorizationStatus(workspaceId, storeId),
        getOfflineSnapshotStatusMetadata(workspaceId, storeId),
      ])
        .then(([summary, operational, authorization, metadata]) => {
          if (!active) return;
          setOfflineQueue(summary);
          setOfflineOperational(operational);
          setOfflineAuthorization(authorization);
          setOfflineStatusSnapshot(metadata);
        })
        .catch(() => {
          if (active) {
            setOfflineQueue({
              pending: 0,
              syncing: 0,
              reconciliation: 0,
              failed: 0,
              syncedRecently: 0,
              total: 0,
            });
          }
        });
    };
    refreshOfflineStatus();
    window.addEventListener("offline:sale-updated", refreshOfflineStatus);
    window.addEventListener("offline:device-updated", refreshOfflineStatus);
    window.addEventListener("offline:snapshot-updated", refreshOfflineStatus);
    return () => {
      active = false;
      window.removeEventListener("offline:sale-updated", refreshOfflineStatus);
      window.removeEventListener("offline:device-updated", refreshOfflineStatus);
      window.removeEventListener("offline:snapshot-updated", refreshOfflineStatus);
    };
  }, [connectivityState, selectedStore?.public_id, selectedWorkspace?.public_id]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setSearchBusy(true);
    setError("");

    const request = isOnline
      ? searchNativePosProducts(debouncedSearch, SEARCH_LIMIT, { signal: controller.signal, pricingMode })
          .then((response) => ({
            items: Array.isArray(response.data?.items) ? response.data.items : [],
            metadata: null,
          }))
      : searchOfflinePosProducts({
          workspacePublicId: selectedWorkspace?.public_id,
          storePublicId: selectedStore?.public_id,
          query: debouncedSearch,
          limit: SEARCH_LIMIT,
        });

    request
      .then((result) => {
        if (!active) return;
        setSearchResults(Array.isArray(result.items) ? result.items : []);
        if (!isOnline) {
          setSnapshotMeta(result.metadata || null);
          if (!result.metadata) {
            setError(t("pos.snapshotMissing"));
          }
        }
      })
      .catch((requestError) => {
        if (requestError?.code !== "ERR_CANCELED" && active) {
          setError(isOnline
            ? getApiError(requestError, t("pos.searchError"))
            : (requestError?.message || t("pos.offlineSearchError")));
        }
      })
      .finally(() => {
        if (active && !controller.signal.aborted) setSearchBusy(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [
    debouncedSearch,
    isOnline,
    pricingMode,
    selectedStore?.public_id,
    selectedWorkspace?.public_id,
    t,
  ]);

  const rotateTransactionKey = useCallback(() => {
    setTransactionKey(createIdempotencyKey("native-pos"));
  }, []);

  const addToCart = useCallback((item, { focusScanner = false } = {}) => {
    const tracked = item.track_inventory !== false;
    const available = normalizedAvailable(item);
    if (tracked && available <= 0) {
      setError(t("pos.noStock", { name: item.product_name }));
      if (focusScanner) refocusScanner();
      return;
    }

    const current = cartRef.current;
    const identityKey = concreteIdentityKey(item.product_public_id, item.product_variant_public_id);
    const existing = current.find((line) => line.identity_key === identityKey);
    let nextCart;

    if (!existing) {
      nextCart = [...current, cartLineFromProduct(item)];
    } else {
      const nextQuantity = existing.quantity + 1;
      const latestAvailable = tracked ? Math.min(existing.available_quantity, available) : null;
      if (nextQuantity > (tracked ? latestAvailable : MAX_LINE_QUANTITY)) {
        nextCart = current.map((line) => (
          line.identity_key === identityKey
            ? {
                ...line,
                physical_quantity: tracked ? Number(item.physical_quantity || line.physical_quantity) : null,
                reserved_quantity: tracked ? Number(item.reserved_quantity || line.reserved_quantity) : null,
                available_quantity: latestAvailable,
              }
            : line
        ));
        cartRef.current = nextCart;
        setCart(nextCart);
        setError(t("pos.onlyAvailable", { count: latestAvailable, name: item.product_name }));
        if (focusScanner) refocusScanner();
        return;
      }

      nextCart = current.map((line) => (
        line.identity_key === identityKey
          ? {
              ...line,
              quantity: nextQuantity,
              price: normalizedPrice(item),
              physical_quantity: tracked ? Number(item.physical_quantity || line.physical_quantity) : null,
              reserved_quantity: tracked ? Number(item.reserved_quantity || line.reserved_quantity) : null,
              available_quantity: latestAvailable,
            }
          : line
      ));
    }

    cartRef.current = nextCart;
    setCart(nextCart);
    rotateTransactionKey();
    setError("");
    setNotice(t("pos.added", { name: item.product_name }));
    if (focusScanner) refocusScanner();
  }, [refocusScanner, rotateTransactionKey, t]);

  const scanBarcode = async (event) => {
    event.preventDefault();
    const code = scannerCode.trim();
    if (!code || scannerBusy) return;

    setScannerBusy(true);
    setError("");
    setNotice("");
    try {
      const item = isOnline
        ? (await lookupNativePosBarcode(code, { pricingMode })).data
        : await lookupOfflinePosBarcode({
            workspacePublicId: selectedWorkspace?.public_id,
            storePublicId: selectedStore?.public_id,
            barcode: code,
          });
      addToCart(item, { focusScanner: true });
      setScannerCode("");
    } catch (requestError) {
      setError(isOnline
        ? getApiError(requestError, t("pos.barcodeMissing"))
        : (requestError?.message || t("pos.offlineBarcodeMissing")));
      setScannerCode("");
      refocusScanner();
    } finally {
      setScannerBusy(false);
    }
  };

  const updateQuantity = (identityKey, nextQuantity, { focusScanner = false } = {}) => {
    const current = cartRef.current;
    const line = current.find((item) => item.identity_key === identityKey);
    if (!line) return;

    const quantity = Math.max(1, Number(nextQuantity || 1));
    if (quantity > (line.track_inventory ? line.available_quantity : MAX_LINE_QUANTITY)) {
      setError(t("pos.currentlyAvailable", { count: line.available_quantity, name: line.product_name }));
      if (focusScanner) refocusScanner();
      return;
    }
    if (quantity === line.quantity) {
      if (focusScanner) refocusScanner();
      return;
    }

    const nextCart = current.map((item) => (
      item.identity_key === identityKey ? { ...item, quantity } : item
    ));
    cartRef.current = nextCart;
    setCart(nextCart);
    rotateTransactionKey();
    setError("");
    if (focusScanner) refocusScanner();
  };

  const removeLine = (identityKey) => {
    const nextCart = cartRef.current.filter((line) => line.identity_key !== identityKey);
    cartRef.current = nextCart;
    setCart(nextCart);
    rotateTransactionKey();
    setError("");
    refocusScanner();
  };

  const clearCart = () => {
    if (!cart.length) return;
    if (!window.confirm(t("pos.clearConfirm"))) return;
    cartRef.current = [];
    setCart([]);
    setDiscountPercent("");
    setCustomDiscountOpen(false);
    setError("");
    setNotice(t("pos.cleared"));
    rotateTransactionKey();
    refocusScanner();
  };

  const cartUnits = useMemo(
    () => cart.reduce((sum, line) => sum + Number(line.quantity || 0), 0),
    [cart],
  );

  const estimatedTotal = useMemo(
    () => cart.reduce(
      (sum, line) => sum + normalizedPrice(line) * Number(line.quantity || 0),
      0,
    ),
    [cart],
  );

  const discountAmount = isOnline && discountPercent && currentDiscountPreview?.valid
    ? Number(currentDiscountPreview.discount_total) : 0;
  const previewSubtotal = isOnline && currentDiscountPreview?.valid
    ? Number(currentDiscountPreview.subtotal) : estimatedTotal;
  const discountedTotal = Math.max(0, previewSubtotal - discountAmount);
  const belowCostLines = currentDiscountPreview?.lines?.filter(
    (line) => line.below_current_average_cost
  ) || [];
  const actorRole = selectedWorkspace?.role?.toLowerCase();
  const discountUnavailable = Boolean(
    (discountPercent || pricingMode === "WHOLESALE") && (
      discountPreviewBusy || discountPreviewError || !currentDiscountPreview?.valid
      || (currentDiscountPreview?.requires_management_approval
        && !["owner", "admin"].includes(actorRole))
      || (belowCostLines.length && actorRole !== "owner")
    )
  );

  const chooseDiscount = (value) => {
    setDiscountPercent(String(value));
    rotateTransactionKey();
  };

  const completeSale = async ({ confirmBelowCost = false } = {}) => {
    if (!cart.length || submitting || (isOnline && discountUnavailable)) return;

    if (isOnline && belowCostLines.length && actorRole === "owner" && !confirmBelowCost) {
      setBelowCostConfirmation({
        final_total: String(discountedTotal),
        estimated_loss: String(belowCostLines.reduce(
          (sum, line) => sum + Number(line.estimated_loss || 0), 0
        )),
        below_cost_items: belowCostLines.map((line) => ({
          product_name: line.variant_label
            ? `${line.product_name} — ${line.variant_label}` : line.product_name,
          quantity: line.quantity,
          final_unit_price: line.unit_price,
          current_average_cost: line.current_average_cost,
          estimated_loss: line.estimated_loss,
        })),
      });
      return;
    }

    if (!isOnline) {
      setSubmitting(true);
      setError("");
      setNotice("");
      try {
        const offlineReceipt = await completeOfflineCashSale({
          workspacePublicId: selectedWorkspace?.public_id,
          storePublicId: selectedStore?.public_id,
          snapshotMeta,
          cart,
        });
        invalidateOfflineCatalogRuntimeCache(
          selectedWorkspace?.public_id,
          selectedStore?.public_id,
        );
        setReceipt(offlineReceipt);
        cartRef.current = [];
        setCart([]);
        setSearchQuery("");
        setSearchResults([]);
        setScannerCode("");
        setTransactionKey(createIdempotencyKey("native-pos"));
        setNotice(t("pos.offlineSaved"));
        refocusScanner();
      } catch (offlineError) {
        setError(offlineError?.message || t("pos.offlineSaveError"));
      } finally {
        setSubmitting(false);
      }
      return;
    }

    setSubmitting(true);
    setError("");
    setNotice("");
    try {
      const storeBridgeDeviceId = getSelectedStoreBridgeDeviceId(
        selectedWorkspace?.public_id,
        selectedStore?.public_id,
      );
      const response = await finalizeNativePosSale(
        {
          items: cart.map((line) => ({
            product_public_id: line.product_public_id,
            product_variant_public_id: line.product_variant_public_id,
            quantity: line.quantity,
          })),
          payment_method: "cash",
          ...(discountPercent ? { discount_percent: discountPercent } : {}),
          ...(confirmBelowCost ? { confirm_below_cost: true } : {}),
          pricing_mode: pricingMode,
        },
        transactionKey,
        { storeBridgeDeviceId },
      );

      setReceipt(response.data);
      cartRef.current = [];
      setCart([]);
      setDiscountPercent("");
      setCustomDiscountOpen(false);
      setBelowCostConfirmation(null);
      setSearchQuery("");
      setSearchResults([]);
      setScannerCode("");
      setTransactionKey(createIdempotencyKey("native-pos"));
      setNotice(t(response.data?.idempotent_replay ? "pos.recovered" : "pos.completed"));
      refocusScanner();
    } catch (requestError) {
      const detail = requestError?.response?.data?.detail;
      if (detail?.code === "BELOW_COST_CONFIRMATION_REQUIRED" && actorRole === "owner") {
        setBelowCostConfirmation(detail);
        setError("");
        return;
      }
      if (requestError?.response?.status === 409 && Array.isArray(detail?.items)) {
        const availability = new Map(
          detail.items.map((item) => [concreteIdentityKey(item.product_public_id, item.product_variant_public_id), item]),
        );
        const refreshedCart = cartRef.current.map((line) => {
          const latest = availability.get(line.identity_key);
          if (!latest) return line;
          return {
            ...line,
            physical_quantity: Number(latest.physical_quantity || 0),
            reserved_quantity: Number(latest.reserved_quantity || 0),
            available_quantity: Number(latest.available_quantity || 0),
          };
        });
        cartRef.current = refreshedCart;
        setCart(refreshedCart);
      }
      // Keep the same idempotency key after any failed/unknown response.
      // Retrying the unchanged cart is therefore safe if the first request
      // actually committed but the browser never received its response.
      setBelowCostConfirmation(null);
      if (detail?.code === "WHOLESALE_PRICE_NOT_CONFIGURED") {
        setError(t("pos.wholesaleNotConfiguredFull"));
      } else if (detail?.code === "WHOLESALE_MINIMUM_QUANTITY_NOT_MET") {
        setError(t("pos.wholesaleMinimumFull", { count: detail.minimum_quantity }));
      } else {
        setError(getApiError(requestError, t("pos.completeError")));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrint = async () => {
    if (!receipt) return;
    const storeBridgeDeviceId = getSelectedStoreBridgeDeviceId(
      selectedWorkspace?.public_id,
      selectedStore?.public_id,
    );
    if (!receipt.offline_pending && storeBridgeDeviceId && receipt.sale_public_id) {
      try {
        const result = await queueStoreBridgeSaleReceipt(
          storeBridgeDeviceId,
          receipt.sale_public_id,
          createStoreBridgeJobKey("receipt-reprint"),
        );
        setNotice(t("pos.receiptQueued", { id: result.job_id }));
        return;
      } catch (requestError) {
        setError(getApiError(requestError, t("pos.directPrintError")));
      }
    }
    const paperWidthMm = getPrinterPaperWidth(selectedWorkspace?.public_id, selectedStore?.public_id);
    if (!printNativePosReceipt(receipt, { storeName: selectedStore?.name, paperWidthMm })) {
      setError(t("pos.blockedPrint"));
    }
  };

  return (
    <MainLayout>
      <PageHeader
        eyebrow={t("pos.eyebrow")}
        title={t("nav.pos")}
        description={t("pos.subtitle")}
        status={{ label: selectedStore?.name || t("pos.currentStore"), color: "info", variant: "outlined" }}
        actions={(
          <Stack direction="row" gap={1} flexWrap="wrap" justifyContent="flex-end">
            {offlineQueue.pending > 0 && (
              <Chip
                label={t("pos.pendingSync", { count: offlineQueue.pending })}
                color="warning"
                variant="outlined"
                sx={{ bgcolor: "#fff", height: 42, fontWeight: 850 }}
              />
            )}
            {offlineQueue.syncing > 0 && (
              <Chip
                label={t("pos.syncing", { count: offlineQueue.syncing })}
                color="info"
                variant="outlined"
                sx={{ bgcolor: "#fff", height: 42, fontWeight: 850 }}
              />
            )}
            {offlineQueue.reconciliation > 0 && (
              <Chip
                label={t("pos.reconciliation", { count: offlineQueue.reconciliation })}
                color="error"
                variant="outlined"
                sx={{ bgcolor: "#fff", height: 42, fontWeight: 850 }}
              />
            )}
            {offlineQueue.failed > 0 && (
              <Chip
                label={t("pos.permanentFailures", { count: offlineQueue.failed })}
                color="error"
                variant="filled"
                sx={{ height: 42, fontWeight: 850 }}
              />
            )}
            <Chip
              label={t("pos.cartUnits", { count: cartUnits })}
              icon={<ShoppingCartRoundedIcon />}
              variant="outlined"
              sx={{ bgcolor: "#fff", height: 42, fontWeight: 850 }}
            />
          </Stack>
        )}
      />

      {!isOnline && (
        <Alert severity="warning" sx={{ mb: 2.25, borderRadius: 3 }}>
          {offlineAuthActive
            ? t("pos.offlineAuthorized", { timestamp: snapshotMeta?.cached_at ? t("pos.snapshotFrom", { date: new Date(snapshotMeta.cached_at).toLocaleString() }) : "" })
            : t("pos.serverState", { state: connectivityState })}
        </Alert>
      )}

      {(offlineAuthorization.deviceStatus !== "not_enrolled" || offlineQueue.total > 0) && (
        <Paper
          variant="outlined"
          sx={{ mb: 2.25, p: 2, borderRadius: 3, borderColor: "divider" }}
        >
          <Stack spacing={1.25}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1} flexWrap="wrap">
              <Chip size="small" label={t("pos.apiStatus", { state: connectivityState })} color={isOnline ? "success" : "warning"} variant="outlined" />
              <Chip
                size="small"
                label={t("pos.deviceStatus", { state: offlineAuthorization.deviceStatus })}
                color={offlineAuthorization.deviceStatus === "active" ? "success" : "error"}
                variant="outlined"
              />
              <Chip
                size="small"
                label={t("pos.grantStatus", { state: offlineAuthorization.grantStatus })}
                color={offlineAuthorization.grantStatus === "active" ? "success" : "warning"}
                variant="outlined"
              />
              <Chip size="small" label={t("pos.pending", { count: offlineQueue.pending })} variant="outlined" />
              <Chip size="small" label={t("pos.syncingCount", { count: offlineQueue.syncing })} variant="outlined" />
              <Chip size="small" label={t("pos.reconciliationCount", { count: offlineQueue.reconciliation })} color={offlineQueue.reconciliation ? "error" : "default"} variant="outlined" />
              <Chip size="small" label={t("pos.failureCount", { count: offlineQueue.failed })} color={offlineQueue.failed ? "error" : "default"} variant="outlined" />
            </Stack>

            <Typography variant="caption" color="text.secondary">
              {t("pos.lastSync", { clean: localDateTime(offlineOperational.lastSuccessfulSyncAt), ack: localDateTime(offlineOperational.lastAcknowledgedSyncAt), snapshot: localDateTime(offlineStatusSnapshot?.cached_at), count: offlineQueue.syncedRecently })}
            </Typography>

            {offlineAuthorization.authorizationError && offlineAuthorization.authorizationState !== "active" && (
              <Alert severity="error" sx={{ py: 0.5 }}>
                {t("pos.authorizationError", { state: offlineAuthorization.authorizationState, error: offlineAuthorization.authorizationError })}
              </Alert>
            )}

            {offlineOperational.deviceMismatchedPending > 0 && (
              <Alert severity="error" sx={{ py: 0.5 }}>
                {t("pos.deviceMismatch", { count: offlineOperational.deviceMismatchedPending })}
              </Alert>
            )}

            {offlineOperational.cashierMismatchedPending > 0 && (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                {t("pos.cashierMismatch", { count: offlineOperational.cashierMismatchedPending })}
              </Alert>
            )}

            {(showAllOfflineIssues ? offlineOperational.sales : offlineOperational.sales.slice(0, 8)).map((sale) => (
              <Box
                key={sale.offline_transaction_id}
                sx={{ p: 1.25, border: "1px solid", borderColor: "divider", borderRadius: 2.25 }}
              >
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={0.5}>
                  <Typography variant="body2" fontWeight={850} sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
                    {sale.offline_transaction_id}
                  </Typography>
                  <Chip
                    size="small"
                    label={sale.status}
                    color={sale.status === "PENDING" ? "warning" : sale.status === "SYNCING" ? "info" : "error"}
                    variant="outlined"
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {localDateTime(sale.transaction?.sold_at || sale.created_at)}
                  {sale.canonical_invoice_number ? ` · ${t("pos.invoiceRef", { number: sale.canonical_invoice_number })}` : ""}
                  {sale.canonical_sale_public_id ? ` · ${t("pos.saleRef", { id: sale.canonical_sale_public_id })}` : ""}
                  {sale.device_mismatch ? ` · ${t("pos.boundOtherDevice")}` : ""}
                  {sale.cashier_mismatch ? ` · ${t("pos.otherCashier")}` : ""}
                </Typography>
                {(sale.reconciliation || []).map((item) => (
                  <Typography key={`${sale.offline_transaction_id}:${item.product_public_id}:${item.product_variant_public_id || "simple"}`} variant="body2" sx={{ mt: 0.5 }}>
                    {t("pos.reconciliationLine", { name: item.product_name, sold: item.requested_quantity, deducted: item.deducted_quantity, shortfall: item.shortfall_quantity, reasons: (item.reasons || []).map(readableOfflineReason).join(", ") })}
                  </Typography>
                ))}
                {sale.status === "FAILED_PERMANENTLY" && (sale.transaction?.items || []).map((item) => (
                  <Typography
                    key={`${sale.offline_transaction_id}:failed:${item.product_public_id}:${item.product_variant_public_id || "simple"}`}
                    variant="body2"
                    sx={{ mt: 0.5 }}
                  >
                    {t("pos.physicalLine", { name: item.product_name || item.product_public_id, quantity: item.quantity, price: formatMoney(item.unit_price) })}
                  </Typography>
                ))}
                {sale.sync_error_message && (
                  <Typography variant="body2" color="error.main" sx={{ mt: 0.5 }}>
                    {sale.sync_error_code ? `${sale.sync_error_code}: ` : ""}{sale.sync_error_message}
                  </Typography>
                )}
              </Box>
            ))}
            {offlineOperational.sales.length > 8 && (
              <Button
                size="small"
                variant="text"
                onClick={() => setShowAllOfflineIssues((current) => !current)}
                sx={{ alignSelf: "flex-start" }}
              >
                {showAllOfflineIssues
                  ? t("pos.recentIssues")
                  : t("pos.allIssues", { count: offlineOperational.sales.length })}
              </Button>
            )}
          </Stack>
        </Paper>
      )}

      {error && <Alert severity="error" sx={{ mb: 2.25 }} onClose={() => setError("")}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2.25 }} onClose={() => setNotice("")}>{notice}</Alert>}

      {isOnline && (
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.25 }}>
          <Typography fontWeight={850}>{t("pos.pricingMode")}</Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={pricingMode}
            onChange={(_, value) => {
              if (!value || value === pricingMode) return;
              setPricingMode(value);
              rotateTransactionKey();
              setError("");
            }}
          >
            <ToggleButton value="RETAIL">{t("pos.retail")}</ToggleButton>
            <ToggleButton value="WHOLESALE">{t("pos.wholesale")}</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.15fr) minmax(390px, 0.85fr)" },
          gap: 2.5,
          alignItems: "start",
        }}
      >
        <Stack gap={2.5}>
          <Paper
            component="form"
            onSubmit={scanBarcode}
            sx={{
              p: { xs: 2, sm: 2.5 },
              border: "1px solid #bfdbfe",
              borderRadius: 4,
              background: "linear-gradient(135deg, #eff6ff 0%, #ffffff 56%, #eef2ff 100%)",
              boxShadow: "0 16px 40px rgba(37, 99, 235, 0.08)",
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center" mb={1.5}>
              <Box sx={{ width: 44, height: 44, borderRadius: 2.5, display: "grid", placeItems: "center", bgcolor: "#2563eb", color: "white" }}>
                <QrCodeScannerRoundedIcon />
              </Box>
              <Box>
                <Typography variant="h6">{t("pos.scanBarcode")}</Typography>
                <Typography variant="body2" color="text.secondary">{t("pos.scanHelp")}</Typography>
              </Box>
            </Stack>
            <TextField
              inputRef={scannerRef}
              fullWidth
              autoComplete="off"
              placeholder={t("pos.scanPlaceholder")}
              value={scannerCode}
              onChange={(event) => setScannerCode(event.target.value)}
              disabled={scannerBusy || submitting}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><QrCodeScannerRoundedIcon color="primary" /></InputAdornment>
                ),
                endAdornment: scannerBusy ? (
                  <InputAdornment position="end"><CircularProgress size={20} /></InputAdornment>
                ) : null,
              }}
            />
          </Paper>

          <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 4, border: "1px solid #e2e8f0" }}>
            <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} justifyContent="space-between" alignItems={{ sm: "center" }} mb={1.75}>
              <Box>
                <Typography variant="h6">{t("pos.findProduct")}</Typography>
                <Typography variant="body2" color="text.secondary">{t("pos.searchHelp")}</Typography>
              </Box>
              {searchBusy && <CircularProgress size={22} />}
            </Stack>
            <TextField
              fullWidth
              placeholder={t("pos.searchCatalog")}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              disabled={submitting}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchRoundedIcon sx={{ color: "#94a3b8" }} /></InputAdornment>
                ),
              }}
            />

            {!searchBusy && searchResults.length === 0 ? (
              <Box sx={{ py: 5, textAlign: "center" }}>
                <Typography fontWeight={850}>{t(debouncedSearch ? "pos.noMatch" : "pos.noProducts")}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{t(debouncedSearch ? "pos.noMatchHelp" : "pos.noProductsHelp")}</Typography>
              </Box>
            ) : (
              <Box sx={{ display: "grid", gap: 1.1, mt: 2, maxHeight: 430, overflowY: "auto", pr: 0.5 }}>
                {groupedSearchResults.map(([productId, choices]) => (
                  <Paper key={productId} variant="outlined" sx={{ p: 1.5, borderRadius: 3, bgcolor: "#fff" }}>
                    <Stack direction="row" gap={1.25} alignItems="center" mb={1}>
                      <PosProductImage product={choices[0]} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography fontWeight={900} noWrap>{choices[0].product_name}</Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {[choices[0].brand, choices[0].category].filter(Boolean).join(" • ")}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack spacing={1}>
                      {choices.map((item) => {
                        const available = normalizedAvailable(item);
                        const identityKey = concreteIdentityKey(item.product_public_id, item.product_variant_public_id);
                        const inCart = cart.find((line) => line.identity_key === identityKey)?.quantity || 0;
                        return (
                          <Stack key={identityKey} direction={{ xs: "column", sm: "row" }} gap={1} justifyContent="space-between" alignItems={{ sm: "center" }}
                            sx={{ pt: 1, borderTop: "1px solid #e2e8f0" }}>
                            <Box sx={{ minWidth: 0 }}>
                              {item.variant_label && <Typography variant="body2" color="primary.main" fontWeight={800}>{item.variant_label}</Typography>}
                              {item.sku && <Typography variant="body2" color="text.secondary">{item.sku}</Typography>}
                              <Stack direction="row" gap={0.75} flexWrap="wrap" mt={0.5}>
                                <Chip size="small" label={item.price == null ? t("pos.wholesaleNotConfigured") : formatMoney(item.price)} color={item.price == null ? "error" : "default"} />
                                {pricingMode === "WHOLESALE" && item.wholesale_min_quantity && (
                                  <Chip size="small" label={t("pos.wholesaleMinimum", { count: item.wholesale_min_quantity })} variant="outlined" />
                                )}
                                {item.track_inventory === false ? <Chip size="small" label={t("catalog.notTracked")} variant="outlined" /> : <Chip
                                  size="small" label={t("pos.available", { count: available })} color={available > 0 ? "success" : "default"} variant="outlined" />}
                                {item.track_inventory !== false && Number(item.reserved_quantity || 0) > 0 && (
                                  <Chip size="small" label={t("pos.reserved", { count: item.reserved_quantity })} color="warning" variant="outlined" />
                                )}
                                {inCart > 0 && <Chip size="small" label={t("pos.inCart", { count: inCart })} color="primary" />}
                              </Stack>
                            </Box>
                            <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={() => addToCart(item)}
                              disabled={item.price == null || (item.track_inventory !== false && (available <= 0 || inCart >= available)) || submitting}>{t("common.add")}</Button>
                          </Stack>
                        );
                      })}
                    </Stack>
                  </Paper>
                ))}
              </Box>
            )}
          </Paper>
        </Stack>

        <Paper
          sx={{
            borderRadius: 4,
            border: "1px solid #dbe3ef",
            overflow: "hidden",
            position: { lg: "sticky" },
            top: { lg: 94 },
            boxShadow: "0 18px 46px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Box sx={{ p: 2.3, bgcolor: "#0f172a", color: "white" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
              <Box>
                <Typography variant="overline" sx={{ color: "#93c5fd", fontWeight: 900 }}>{t("pos.currentSale")}</Typography>
                <Typography variant="h6">{t("pos.cashCart")}</Typography>
              </Box>
              <Tooltip title={t("pos.clearSale")}>
                <span>
                  <IconButton onClick={clearCart} disabled={!cart.length || submitting} sx={{ color: "#cbd5e1" }}>
                    <RestartAltRoundedIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Box>

          <Box sx={{ minHeight: 320, maxHeight: { lg: "calc(100vh - 410px)" }, overflowY: "auto" }}>
            {cart.length === 0 ? (
              <Box sx={{ px: 3, py: 7, textAlign: "center" }}>
                <ShoppingCartRoundedIcon sx={{ fontSize: 48, color: "#cbd5e1" }} />
                <Typography fontWeight={900} sx={{ mt: 1.25 }}>{t("pos.cartEmpty")}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{t("pos.cartEmptyHelp")}</Typography>
              </Box>
            ) : (
              <Stack divider={<Divider />}>
                {cart.map((line) => {
                  const previewLine = currentDiscountPreview?.lines?.find(
                    (item) => item.product_public_id === line.product_public_id
                      && (item.product_variant_public_id || null) === line.product_variant_public_id,
                  );
                  const displayedUnitPrice = Number(previewLine?.unit_price ?? line.price);
                  return (
                  <Box key={line.identity_key} sx={{ p: 2 }}>
                    <Stack direction="row" justifyContent="space-between" gap={1.25}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography fontWeight={900} noWrap>{line.product_name}</Typography>
                        {line.variant_label && <Typography variant="body2" color="primary.main" fontWeight={800} noWrap>{line.variant_label}</Typography>}
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                          {[line.brand, line.sku].filter(Boolean).join(" • ") || line.category}
                        </Typography>
                      </Box>
                      <IconButton size="small" aria-label={t("pos.removeProduct", { name: line.product_name })} onClick={() => removeLine(line.identity_key)} disabled={submitting}>
                        <DeleteOutlineRoundedIcon fontSize="small" />
                      </IconButton>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1.4} gap={1}>
                      <Stack direction="row" alignItems="center" spacing={0.25}>
                        <IconButton
                          size="small"
                          onClick={() => updateQuantity(line.identity_key, line.quantity - 1, { focusScanner: true })}
                          disabled={submitting || line.quantity <= 1}
                        >
                          <RemoveRoundedIcon fontSize="small" />
                        </IconButton>
                        <TextField
                          value={line.quantity}
                          onChange={(event) => updateQuantity(line.identity_key, Number(event.target.value))}
                          inputProps={{ min: 1, max: line.track_inventory ? line.available_quantity : MAX_LINE_QUANTITY, inputMode: "numeric", style: { textAlign: "center" } }}
                          type="number"
                          size="small"
                          disabled={submitting}
                          sx={{ width: 76, "& input": { py: 0.8 } }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => updateQuantity(line.identity_key, line.quantity + 1, { focusScanner: true })}
                          disabled={submitting || line.quantity >= (line.track_inventory ? line.available_quantity : MAX_LINE_QUANTITY)}
                        >
                          <AddRoundedIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography fontWeight={900}>{formatMoney(displayedUnitPrice * line.quantity)}</Typography>
                        <Typography variant="caption" color="text.secondary">{t("pos.each", { price: formatMoney(displayedUnitPrice) })}</Typography>
                      </Box>
                    </Stack>
                    <Typography variant="caption" color={line.track_inventory && line.quantity > line.available_quantity ? "error.main" : "text.secondary"} sx={{ display: "block", mt: 0.65 }}>
                      {line.track_inventory
                        ? t("pos.availableNow", { count: line.available_quantity, reserved: line.reserved_quantity > 0 ? t("pos.reservedOrders", { count: line.reserved_quantity }) : "" })
                        : t("pos.inventoryNotTracked")}
                    </Typography>
                  </Box>
                  );
                })}
              </Stack>
            )}
          </Box>

          <Divider />
          <Box sx={{ p: 2.3, bgcolor: "#f8fafc" }}>
            <Stack gap={0.8} mb={1.75}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto",
                  alignItems: "center",
                  columnGap: 2,
                }}
              >
                <Typography color="text.secondary">{t("pos.items")}</Typography>
                <Typography fontWeight={800} textAlign="right">{cartUnits}</Typography>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) auto",
                  alignItems: "end",
                  columnGap: 2,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography color="text.secondary">{t(isOnline && discountPercent ? "pos.subtotal" : "pos.estimatedTotal")}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {isOnline
                      ? t("pos.authoritativeTotal")
                      : t("pos.snapshotTotal")}
                  </Typography>
                </Box>
                <Typography variant="h5" textAlign="right">{formatMoney(previewSubtotal)}</Typography>
              </Box>
              {isOnline ? (
                <Box sx={{ pt: 1.1 }}>
                  {pricingMode === "WHOLESALE" && discountPreviewError && !discountPercent && (
                    <Alert severity="error" sx={{ mb: 1 }}>{discountPreviewError}</Alert>
                  )}
                  {pricingMode === "WHOLESALE" && discountPreviewBusy && !discountPercent && (
                    <Typography variant="caption">{t("pos.validating")}</Typography>
                  )}
                  <Typography fontWeight={800} mb={0.75}>{t("pos.discount")}</Typography>
                  {discountCapabilityError ? (
                    <Alert severity="warning" sx={{ mb: 1 }}>{discountCapabilityError}</Alert>
                  ) : discountCapability?.can_apply_discount ? (
                    <>
                      <Stack direction="row" gap={0.75} flexWrap="wrap">
                        {quickDiscountPercents(discountCapability.effective_max_discount_percent).map((value) => (
                          <Button
                            key={value}
                            size="small"
                            variant={Number(discountPercent) === value ? "contained" : "outlined"}
                            onClick={() => chooseDiscount(value)}
                            disabled={!cart.length || submitting}
                          >
                            {value}%
                          </Button>
                        ))}
                        <Button
                          size="small"
                          variant={customDiscountOpen ? "contained" : "outlined"}
                          onClick={() => setCustomDiscountOpen((open) => !open)}
                          disabled={!cart.length || submitting}
                        >
                          {t("pos.custom")}
                        </Button>
                      </Stack>
                      {customDiscountOpen && (
                        <TextField
                          label={t("pos.customDiscount")}
                          value={discountPercent}
                          onChange={(event) => chooseDiscount(event.target.value)}
                          type="number"
                          size="small"
                          fullWidth
                          disabled={!cart.length || submitting}
                          inputProps={{ min: 0, max: Number(discountCapability.effective_max_discount_percent), step: 0.01 }}
                          helperText={t("pos.maximumAllowed", { percent: discountCapability.effective_max_discount_percent })}
                          sx={{ mt: 1.2 }}
                        />
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">{t("pos.discountUnavailable")}</Typography>
                  )}
                  {discountPercent && (
                    <Button size="small" onClick={() => { chooseDiscount(""); setCustomDiscountOpen(false); }} disabled={submitting} sx={{ mt: 0.6 }}>
                      {t("pos.clearDiscount")}
                    </Button>
                  )}
                  {discountPercent && (
                    <>
                      <Stack direction="row" justifyContent="space-between" mt={1}>
                        <Typography>{t("pos.discountPercent", { percent: discountPercent })}</Typography>
                        <Typography fontWeight={800}>-{formatMoney(discountAmount)}</Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between" mt={0.5}>
                        <Typography variant="h6">{t("common.total")}</Typography>
                        <Typography variant="h5">{formatMoney(discountedTotal)}</Typography>
                      </Stack>
                      {discountPreviewBusy && <Typography variant="caption">{t("pos.validating")}</Typography>}
                      {discountPreviewError && <Alert severity="error" sx={{ mt: 1 }}>{discountPreviewError}</Alert>}
                      {currentDiscountPreview && !currentDiscountPreview.valid && <Alert severity="error" sx={{ mt: 1 }}>{t("pos.discountNotAllowed")}</Alert>}
                      {currentDiscountPreview?.requires_management_approval
                        && !["owner", "admin"].includes(actorRole)
                        && !belowCostLines.length
                        && <Alert severity="warning" sx={{ mt: 1 }}>{t("pos.managementReview")}</Alert>}
                      {currentDiscountPreview?.valid && belowCostLines.length > 0 && actorRole === "owner" && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          <Typography fontWeight={900}>{t("pos.belowCost")}</Typography>
                          <Typography variant="body2">{t("pos.belowCostHelp")}</Typography>
                          {belowCostLines.map((line) => (
                            <Typography key={concreteIdentityKey(line.product_public_id, line.product_variant_public_id)} variant="body2" sx={{ mt: 0.5 }}>
                              {t("pos.belowCostLine", { name: `${line.product_name}${line.variant_label ? ` — ${line.variant_label}` : ""}`, price: formatMoney(line.unit_price), cost: formatMoney(line.current_average_cost), loss: formatMoney(line.estimated_loss) })}
                            </Typography>
                          ))}
                        </Alert>
                      )}
                      {currentDiscountPreview?.valid && belowCostLines.length > 0 && actorRole === "staff"
                        && <Alert severity="error" sx={{ mt: 1 }}>{t("pos.staffReject")}</Alert>}
                      {currentDiscountPreview?.valid && belowCostLines.length > 0 && actorRole === "admin"
                        && <Alert severity="warning" sx={{ mt: 1 }}>{t("pos.ownerApproval")}</Alert>}
                    </>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">{t("pos.discountOnline")}</Typography>
              )}
            </Stack>

            <Button
              fullWidth
              size="large"
              variant="contained"
              startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <PaymentsRoundedIcon />}
              onClick={() => completeSale()}
              disabled={
                !cart.length
                || submitting
                || (isOnline && discountUnavailable)
                || cart.some((line) => line.track_inventory && line.quantity > line.available_quantity)
                || (!isOnline && (!snapshotMeta || cart.some((line) => !line.sale_claim)))
              }
              sx={{ minHeight: 54, fontSize: "1rem" }}
            >
              {submitting
                ? t("pos.completing")
                : isOnline
                  ? t("pos.completeCash")
                  : t("pos.completeOfflineCash")}
            </Button>
            <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mt: 1.2 }}>
              {t("pos.signedIn", { name: user?.username || t("pos.cashier"), message: t(isOnline ? "pos.onlineStock" : "pos.offlineDurable") })}
            </Typography>
          </Box>
        </Paper>
      </Box>

      <Dialog open={Boolean(belowCostConfirmation)} onClose={() => setBelowCostConfirmation(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t("pos.belowCost")}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>{t("pos.belowCostWarning")}</Alert>
          <Stack gap={1.5}>
            {(belowCostConfirmation?.below_cost_items || []).map((item, index) => (
              <Paper key={`${item.product_name}-${index}`} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Typography fontWeight={800}>{item.product_name}</Typography>
                <Typography variant="body2">{t("pos.finalPrice", { price: formatMoney(item.final_unit_price) })}</Typography>
                <Typography variant="body2">{t("pos.averageCost", { cost: formatMoney(item.current_average_cost) })}</Typography>
                <Typography variant="body2" color="error.main">{t("pos.estimatedLoss", { loss: formatMoney(item.estimated_loss) })}</Typography>
              </Paper>
            ))}
            <Stack direction="row" justifyContent="space-between"><Typography fontWeight={800}>{t("pos.finalTotal")}</Typography><Typography fontWeight={900}>{formatMoney(belowCostConfirmation?.final_total)}</Typography></Stack>
            <Stack direction="row" justifyContent="space-between"><Typography fontWeight={800}>{t("pos.loss")}</Typography><Typography fontWeight={900} color="error.main">{formatMoney(belowCostConfirmation?.estimated_loss)}</Typography></Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setBelowCostConfirmation(null)} disabled={submitting}>{t("common.cancel")}</Button>
          <Button variant="contained" color="warning" onClick={() => completeSale({ confirmBelowCost: true })} disabled={submitting}>
            {t("pos.sellAnyway")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(receipt)}
        onClose={() => {
          setReceipt(null);
          refocusScanner();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <Box sx={{ width: 42, height: 42, borderRadius: 2.5, display: "grid", placeItems: "center", bgcolor: "#dcfce7", color: "#15803d" }}>
              <ReceiptLongRoundedIcon />
            </Box>
            <Box>
              <Typography variant="h6">{t(receipt?.offline_pending ? "pos.offlineCompleted" : "pos.saleCompleted")}</Typography>
              <Typography variant="body2" color="text.secondary">{receipt?.invoice_number || receipt?.sale_public_id}</Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {receipt?.offline_pending && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t("pos.provisional")}
            </Alert>
          )}
          <Stack gap={1.6}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "88px minmax(0, 1fr)", sm: "120px minmax(0, 1fr)" },
                alignItems: "center",
                columnGap: 2,
                rowGap: 1.15,
              }}
            >
              <Typography color="text.secondary">{t("common.date")}</Typography>
              <Typography fontWeight={750} textAlign="right">{formatDate(receipt?.sold_at)}</Typography>
              <Typography color="text.secondary">{t("pos.payment")}</Typography>
              <Typography fontWeight={750} textAlign="right">{t("pos.cashPaid")}</Typography>
              {receipt?.offline_pending && (
                <>
                  <Typography color="text.secondary">{t("pos.syncStatus")}</Typography>
                  <Typography fontWeight={750} textAlign="right" color="warning.main">{t("pos.pendingAbos")}</Typography>
                </>
              )}
            </Box>
            <Divider />
            <Stack gap={1.25}>
              {(receipt?.items || []).map((item, index) => (
                <Box
                  key={`${concreteIdentityKey(item.product_public_id, item.product_variant_public_id)}-${index}`}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) auto",
                    alignItems: "start",
                    columnGap: 2,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={800}>{item.product_name}</Typography>
                    {item.variant_label && <Typography variant="body2" color="primary.main" fontWeight={750}>{item.variant_label}</Typography>}
                    {item.sku && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t("pos.sku", { value: item.sku })}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" display="block">
                      {item.quantity} × {formatMoney(item.unit_price, receipt?.currency)}
                    </Typography>
                  </Box>
                  <Typography fontWeight={850} textAlign="right">
                    {formatMoney(item.line_total, receipt?.currency)}
                  </Typography>
                </Box>
              ))}
            </Stack>
            <Divider />
            {!receipt?.offline_pending && Number(receipt?.discount_total || 0) > 0 && (
              <Stack gap={0.5}>
                <Stack direction="row" justifyContent="space-between"><Typography>{t("pos.subtotal")}</Typography><Typography>{formatMoney(receipt?.subtotal, receipt?.currency)}</Typography></Stack>
                <Stack direction="row" justifyContent="space-between"><Typography>{t("pos.discount")}</Typography><Typography>-{formatMoney(receipt?.discount_total, receipt?.currency)}</Typography></Stack>
              </Stack>
            )}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr auto",
                alignItems: "center",
                columnGap: 2,
              }}
            >
              <Typography variant="h6">{t("common.total")}</Typography>
              <Typography variant="h5" textAlign="right">{formatMoney(receipt?.total_amount, receipt?.currency)}</Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setReceipt(null); refocusScanner(); }}>{t("pos.newSale")}</Button>
          <Button variant="contained" startIcon={<ReceiptLongRoundedIcon />} onClick={handlePrint}>{t("pos.printReceipt")}</Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
}
